// Üzenet IDŐZÍTÉS / ÁTIDŐZÍTÉS dialógusa — a közös ConfirmDialog nem fogad extra tartalmat, ezért kis
// dedikált dialógus a közös Dialog-primitíveken (az asset-reject-dialog mintája), a globális `DateTimePicker`-rel
// (Calendar + idő-mező) — egységes a rendszer többi dátumválasztójával. A mező HELYI időt tart; a hívó a
// megerősítéskor kapja a helyi stringet és konvertál UTC ISO-ra (localInputToIso) a wire-hez. Kliens-oldali őr:
// kötelező + jövőbeli időpont (a BFF 422-je a fal).
import { formatInTimeZone } from 'date-fns-tz';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { isoToLocalInput, localInputToIso } from '@/features/messages/form';
import { useTimeZone } from '@/lib/format-datetime';
import { nextDefaultStart } from '@/lib/workday';

export interface MessageScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Az üzenet tárgya (a leíró szövegbe). */
  subject: string;
  /** Átidőzítésnél a jelenlegi időpont (UTC ISO) — előtöltésre; új időzítésnél null. */
  currentScheduledAt: string | null;
  /** Folyamatban lévő mentés — a gombok tiltva, a bezárás blokkolva. */
  pending?: boolean;
  /** Megerősítés a HELYI datetime-local értékkel (a hívó konvertál UTC ISO-ra). */
  onConfirm: (scheduledAtLocal: string) => void;
}

export function MessageScheduleDialog({
  open,
  onOpenChange,
  subject,
  currentScheduledAt,
  pending = false,
  onConfirm,
}: MessageScheduleDialogProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const zone = useTimeZone();
  const [local, setLocal] = useState('');
  const [error, setError] = useState<string | null>(null);

  /**
   * Nyitáskor előtöltés: ÁTIDŐZÍTÉSNÉL a jelenlegi időpont, ÚJ időzítésnél a KÖVETKEZŐ munkanap-kezdés
   * (ma 08:00, ha még nem múlt el — különben holnap 08:00).
   *
   * MIÉRT NEM ÜRES (a korábbi viselkedés): a mező üresen indult, tehát a felhasználó minden alkalommal maga
   * írta be az órát, és az idő-fele 00:00-ra esett. A „jövőbeli" szempont is szükséges: egy vak „ma 08:00"
   * délután MÁR HIBÁS mezővel nyitná a dialógust (piros hiba, mielőtt bármit tett volna).
   */
  useEffect(() => {
    if (!open) return;
    const preset = isoToLocalInput(currentScheduledAt);
    if (preset !== '') {
      setLocal(preset);
    } else {
      const today = formatInTimeZone(new Date(), zone, 'yyyy-MM-dd');
      const nowMinutes =
        Number(formatInTimeZone(new Date(), zone, 'H')) * 60 +
        Number(formatInTimeZone(new Date(), zone, 'm'));
      setLocal(nextDefaultStart(today, nowMinutes));
    }
    setError(null);
  }, [open, currentScheduledAt, zone]);

  const confirm = (): void => {
    const iso = localInputToIso(local);
    if (iso === '') {
      setError(t('validation:messages.scheduledAt.required'));
      return;
    }
    if (iso <= new Date().toISOString()) {
      setError(t('validation:messages.scheduledAt.future'));
      return;
    }
    setError(null);
    onConfirm(local);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('messages.schedule.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <DialogDescription>{t('messages.schedule.desc', { subject })}</DialogDescription>
          <div className="space-y-1.5">
            <Label>{t('messages.fields.scheduledAt')}</Label>
            <DateTimePicker
              value={local}
              onChange={(v) => {
                setLocal(v);
                setError(null);
              }}
              label={t('messages.fields.scheduledAt')}
              language={i18n.language}
              disabled={pending}
            />
            <p className="text-muted-foreground text-xs">{t('messages.fields.scheduledAtHint')}</p>
            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            {t('messages.cancel')}
          </Button>
          <Button variant="brand" type="button" onClick={confirm} disabled={pending}>
            {pending ? t('messages.schedule.working') : t('messages.schedule.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
